import { NavLink, Outlet, useNavigate, useLocation, matchPath } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUnread } from "@/context/UnreadContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Users, Bike, ClipboardList, Wallet, Settings,
  LogOut, Menu, Package, CalendarDays, Banknote, ShieldAlert, MessageCircle,
  Sun, Moon, Monitor, ChevronDown, Search, ChevronRight, ScanLine,
} from "lucide-react";
import { cn } from "@/lib/utils";
import NotificationCenter from "@/components/NotificationCenter";
import { BrandMark, EraMonogram } from "@/components/BrandMark";

const NAV_GROUPS = [
  {
    label: "Operasyon",
    items: [
      { to: "/", label: "Ana Sayfa", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
      { to: "/randevular", label: "Randevular", icon: CalendarDays, testid: "nav-appointments" },
      { to: "/servis-kayitlari", label: "Servis Kayıtları", icon: ClipboardList, testid: "nav-services", unreadKey: "services" },
      { to: "/musteriler", label: "Müşteriler", icon: Users, testid: "nav-customers" },
      { to: "/motorlar", label: "Motorlar", icon: Bike, testid: "nav-motorcycles" },
    ],
  },
  {
    label: "İletişim",
    items: [
      { to: "/genel-grup", label: "Genel Grup", icon: MessageCircle, testid: "nav-general-group", unreadKey: "general" },
    ],
  },
  {
    label: "Finans",
    items: [
      { to: "/stok", label: "Stok / Parça", icon: Package, testid: "nav-inventory" },
      { to: "/kasa", label: "Kasa", icon: Banknote, testid: "nav-cash" },
      { to: "/odeme-bekleyenler", label: "Ödemesi Bekleyenler", icon: Wallet, testid: "nav-unpaid" },
    ],
  },
  {
    label: "Yönetim",
    items: [
      { to: "/admin/mukerrer", label: "Mükerrer İnceleme", icon: ShieldAlert, testid: "nav-duplicates", adminOnly: true },
      { to: "/barkod-test", label: "Barkod Testi", icon: ScanLine, testid: "nav-barkod-test", adminOnly: true },
      { to: "/ayarlar", label: "Ayarlar", icon: Settings, testid: "nav-settings" },
    ],
  },
];

// Route → sayfa başlığı ve breadcrumb bağlamı
const ROUTE_META = [
  { path: "/",                             group: "Operasyon", title: "Komuta Merkezi" },
  { path: "/randevular",                   group: "Operasyon", title: "Randevular" },
  { path: "/servis-kayitlari/yeni",        group: "Operasyon", title: "Yeni İş Kartı" },
  { path: "/servis-kayitlari/:id",         group: "Operasyon", title: "İş Kartı Detayı" },
  { path: "/servis-kayitlari",             group: "Operasyon", title: "İş Emirleri" },
  { path: "/musteriler/:id",               group: "Operasyon", title: "Müşteri Detayı" },
  { path: "/musteriler",                   group: "Operasyon", title: "Müşteriler" },
  { path: "/motorlar/:id",                 group: "Operasyon", title: "Motor Detayı" },
  { path: "/motorlar",                     group: "Operasyon", title: "Motorlar" },
  { path: "/genel-grup",                   group: "İletişim",  title: "Genel Servis Grubu" },
  { path: "/stok",                         group: "Finans",    title: "Stok / Parça" },
  { path: "/kasa",                         group: "Finans",    title: "Kasa" },
  { path: "/odeme-bekleyenler",            group: "Finans",    title: "Ödemesi Bekleyenler" },
  { path: "/admin/mukerrer",               group: "Yönetim",   title: "Mükerrer İnceleme" },
  { path: "/barkod-test",                  group: "Yönetim",   title: "Barkod Testi" },
  { path: "/ayarlar",                      group: "Yönetim",   title: "Ayarlar" },
];

function resolveContext(pathname) {
  for (const r of ROUTE_META) {
    if (matchPath({ path: r.path, end: true }, pathname)) return r;
  }
  return { group: "", title: "" };
}

function SidebarContent({ onNavigate, showBrand = true }) {
  const { total: unreadTotal, generalUnread } = useUnread();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="flex flex-col h-full" data-testid="sidebar">
      {showBrand && <BrandMark variant="sidebar" />}
      <nav className="flex-1 overflow-y-auto py-2 px-3 no-scrollbar" data-testid="sidebar-nav">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((n) => !n.adminOnly || isAdmin);
          if (items.length === 0) return null;
          return (
            <div key={group.label} className="mb-4">
              <div className="flex items-center gap-2 px-2.5 py-1.5">
                <span className="h-px flex-1 bg-border/60" />
                <span className="text-[9.5px] uppercase tracking-[0.22em] font-bold text-muted-foreground">
                  {group.label}
                </span>
                <span className="h-px w-3 bg-border/60" />
              </div>
              {items.map(({ to, label, icon: Icon, end, testid, unreadKey }) => {
                const badge = unreadKey === "services" ? unreadTotal : (unreadKey === "general" ? generalUnread : 0);
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={testid}
                    onClick={onNavigate}
                    className={({ isActive }) => cn(
                      "era-nav-item era-tap relative flex items-center gap-3 pl-4 pr-3 py-2 rounded-md text-[13.5px] font-medium transition-colors group",
                      isActive
                        ? "bg-primary/10 text-foreground"
                        : "text-foreground/70 hover:bg-accent hover:text-foreground",
                    )}
                    data-active-observer=""
                  >
                    {({ isActive }) => (
                      <>
                        {/* Sol aktif çubuk — animasyonlu */}
                        <span
                          aria-hidden
                          className={cn(
                            "absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full transition-transform duration-200 origin-center",
                            isActive ? "bg-primary scale-y-100" : "bg-primary/0 scale-y-0",
                          )}
                        />
                        <Icon className={cn(
                          "h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5",
                          isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground",
                        )} strokeWidth={2} />
                        <span className={cn("flex-1 truncate", isActive && "font-semibold")}>{label}</span>
                        {badge > 0 && (
                          <span
                            data-testid={`${testid}-unread`}
                            className={cn(
                              "min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-bold leading-none",
                              "bg-destructive text-destructive-foreground",
                            )}
                          >
                            {badge > 99 ? "99+" : badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Alt kullanıcı kartı */}
      <SidebarUserCard onNavigate={onNavigate} />
    </div>
  );
}

function SidebarUserCard({ onNavigate }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = ((user?.full_name || user?.username || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("") || "?").toUpperCase();

  const handleLogout = () => {
    logout();
    onNavigate?.();
    nav("/login", { replace: true });
  };
  const goSettings = () => {
    onNavigate?.();
    nav("/ayarlar");
  };

  return (
    <div className="mt-auto px-3 pb-4 pt-2" data-testid="sidebar-user-card">
      <div className="relative rounded-lg border border-border bg-muted/30 p-3">
        <div className="flex items-center gap-3">
          <div className="relative h-9 w-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs shrink-0">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-foreground truncate leading-tight" data-testid="sidebar-username">
              {user?.full_name || user?.username}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
              {user?.role === "admin" ? "Yönetici" : "Servis Kullanıcı"}
            </div>
          </div>
        </div>
        <div className="mt-2.5 flex items-center gap-1.5">
          <button
            onClick={goSettings}
            className="era-tap flex-1 inline-flex items-center justify-center gap-1.5 h-8 rounded-md border border-border bg-card hover:bg-accent hover:text-accent-foreground text-[11.5px] font-medium transition-colors"
            data-testid="sidebar-settings-btn"
          >
            <Settings className="h-3.5 w-3.5"/>Ayarlar
          </button>
          <button
            onClick={handleLogout}
            className="era-tap inline-flex items-center justify-center h-8 w-8 rounded-md border border-border bg-card hover:bg-destructive hover:text-destructive-foreground hover:border-destructive/40 transition-colors"
            aria-label="Çıkış Yap"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="h-3.5 w-3.5"/>
          </button>
        </div>
      </div>
    </div>
  );
}

function ThemeToggle() {
  const { preference, effective, setPreference } = useTheme() || {};
  if (!setPreference) return null;
  const Icon = effective === "dark" ? Moon : Sun;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="era-tap h-9 w-9" data-testid="theme-toggle-btn" aria-label="Tema seç">
          <Icon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Tema</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={preference} onValueChange={setPreference}>
          <DropdownMenuRadioItem value="light" data-testid="theme-light"><Sun className="h-4 w-4 mr-2 inline"/>Açık</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" data-testid="theme-dark"><Moon className="h-4 w-4 mr-2 inline"/>Koyu</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system" data-testid="theme-system"><Monitor className="h-4 w-4 mr-2 inline"/>Sistem</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HeaderUser({ onLogout, onSettings }) {
  const { user } = useAuth();
  const initials = ((user?.full_name || user?.username || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("") || "?").toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="era-tap flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-md hover:bg-accent transition-colors"
          data-testid="user-menu-btn"
        >
          <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-semibold text-[11px]">
            {initials}
          </div>
          <div className="hidden md:flex flex-col items-start leading-tight">
            <span className="text-[13px] font-semibold text-foreground" data-testid="header-username">
              {(user?.full_name || user?.username || "").split(" ")[0]}
            </span>
            <span className="text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground">
              {user?.role === "admin" ? "Yönetici" : "Servis"}
            </span>
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground hidden md:inline" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-bold text-[11px]">{initials}</div>
          <div className="flex flex-col">
            <span className="text-[13px] font-semibold">{user?.full_name || user?.username}</span>
            <span className="text-[9.5px] font-normal text-muted-foreground uppercase tracking-widest">
              {user?.role === "admin" ? "Yönetici" : "Servis Kullanıcı"}
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSettings} data-testid="user-menu-settings">
          <Settings className="h-4 w-4 mr-2"/>Ayarlar
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onLogout} data-testid="logout-button" className="text-destructive focus:text-destructive">
          <LogOut className="h-4 w-4 mr-2"/>Çıkış Yap
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function HeaderSearch() {
  const nav = useNavigate();
  // Search çubuğu şu an: enter'da /servis-kayitlari?q= sayfasına yönlendiriyor.
  // Command palette YOK — kullanıcı direktifi.
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const q = new FormData(e.currentTarget).get("q");
        if (q && String(q).trim()) {
          nav(`/servis-kayitlari?q=${encodeURIComponent(String(q).trim())}`);
        }
      }}
      className="hidden md:flex items-center gap-2 flex-1 max-w-md"
      role="search"
      data-testid="header-search-form"
    >
      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
        <input
          name="q"
          type="text"
          placeholder="Plaka, servis no, müşteri ara..."
          className="w-full h-9 pl-9 pr-3 text-[13px] rounded-md border border-border bg-muted/40 hover:bg-muted focus:bg-card focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors text-foreground placeholder:text-muted-foreground"
          data-testid="header-search-input"
        />
      </div>
    </form>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const ctx = resolveContext(location.pathname);

  const handleLogout = () => { logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex min-h-screen">
        {/* Masaüstü sidebar */}
        <aside
          className="no-print hidden lg:flex flex-col w-[var(--sidebar-width)] shrink-0 border-r border-border bg-card min-h-screen sticky top-0"
          data-testid="desktop-sidebar"
        >
          <SidebarContent />
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          {/* Header — sayfa bağlamı + arama + ikonlar */}
          <header
            className="no-print sticky top-0 z-30 bg-card/85 backdrop-blur-md border-b border-border"
            data-testid="app-header"
          >
            <div className="flex items-center gap-2 sm:gap-3 h-[var(--header-height)] px-3 sm:px-4 lg:px-6">
              {/* Mobil menü */}
              <div className="lg:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="era-tap" data-testid="open-menu-button" aria-label="Menü">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="p-0 w-80 bg-card">
                    <SidebarContent />
                  </SheetContent>
                </Sheet>
              </div>

              {/* Mobilde kompakt logo — masaüstünde breadcrumb */}
              <div className="lg:hidden">
                <EraMonogram size={30} />
              </div>

              {/* Breadcrumb + sayfa başlığı */}
              <div className="hidden lg:flex items-center gap-2.5 min-w-0">
                {ctx.group && (
                  <>
                    <span className="text-[10.5px] uppercase tracking-[0.22em] text-muted-foreground font-semibold whitespace-nowrap">
                      {ctx.group}
                    </span>
                    <ChevronRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />
                  </>
                )}
                <span className="text-[15px] font-display font-bold text-foreground truncate">
                  {ctx.title}
                </span>
              </div>

              {/* Mobil sayfa başlığı */}
              <div className="lg:hidden min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground leading-none">{ctx.group}</div>
                <div className="text-sm font-display font-bold truncate">{ctx.title}</div>
              </div>

              {/* Arama — masaüstü orta */}
              <div className="hidden lg:flex flex-1 justify-center">
                <HeaderSearch />
              </div>

              {/* Sağ ikon şeridi */}
              <div className="flex items-center gap-0.5 sm:gap-1">
                <NotificationCenter />
                <ThemeToggle />
                <div className="w-px h-6 bg-border/60 mx-1 hidden sm:block" />
                <HeaderUser
                  onLogout={handleLogout}
                  onSettings={() => nav("/ayarlar")}
                />
              </div>
            </div>
          </header>

          {/* Main content */}
          <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto era-fade-up">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
